import { memo, ReactNode, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import usePageTitle from '@/components/hooks/usePageTitle';
import { accountStatusOptions } from '@/constants';
import { useShipmentPastDueRestriction } from '@/hooks/useShipmentPastDueRestriction';
import type {
  ShipmentCalf,
  ShipmentDetail,
  ShipmentDonor,
  ShipmentEmbryo,
  ShipmentRecipient,
  ShipmentSemen,
} from '@/services/shipping/types';

import { ShipmentAdditionalData } from './components/ShipmentAdditionalData';
import { ShipmentCalvesGrid } from './components/ShipmentCalvesGrid';
import { ShipmentDonorGrid } from './components/ShipmentDonorGrid';
import { ShipmentEmbryoGrid } from './components/ShipmentEmbryoGrid';
import { ShipmentForm } from './components/ShipmentForm';
import { ShipmentRecipientGrid } from './components/ShipmentRecipientGrid';
import { ShipmentSemenGrid } from './components/ShipmentSemenGrid';
import { ShipmentToolBar } from './components/ShipmentToolbar';
import ShipmentContext from './hooks/useShipmentContext';

type ShipmentProps = {
  shipment: ShipmentDetail | null | undefined;
  embryos: ShipmentEmbryo[] | null | undefined;
  semen: ShipmentSemen[] | null | undefined;
  donors: ShipmentDonor[] | null | undefined;
  recipients: ShipmentRecipient[] | null | undefined;
  calves: ShipmentCalf[] | null | undefined;
  className?: string;
  children: ReactNode;
};

export const Shipment = ({
  shipment,
  calves,
  donors,
  embryos,
  recipients,
  semen,
  className,
  children,
}: ShipmentProps) => {
  const { setTitle } = usePageTitle();
  const { t } = useTranslation();
  const { restrictionEnabled, getOwnerStatus } =
    useShipmentPastDueRestriction();

  const shipmentNumber = shipment?.shipmentNumber;
  const owner = shipment?.owner;

  useEffect(() => {
    if (shipmentNumber) {
      setTitle(shipmentNumber, `${shipmentNumber} | Shipping`);
    }

    return () => {
      setTitle('');
    };
  }, [setTitle, shipmentNumber]);

  const closedNoNewBusinessValue = useMemo(
    () =>
      accountStatusOptions.find(
        (opt) => opt.label === 'Closed - No New Business',
      )?.value ??
      accountStatusOptions.find((opt) => opt.value === 100000003)?.value ??
      null,
    [],
  );

  const isOverAging60 = useMemo(() => {
    return !!owner?.aging60;
  }, [owner?.aging60]);

  const isPastDue = useMemo(() => {
    return !!(
      owner?.aging90 ||
      owner?.aging180 ||
      owner?.aging365 ||
      (closedNoNewBusinessValue !== null &&
        owner?.accountStatus === closedNoNewBusinessValue)
    );
  }, [
    owner?.aging90,
    owner?.aging180,
    owner?.aging365,
    owner?.accountStatus,
    closedNoNewBusinessValue,
  ]);

  const restrictionStatus = useMemo(
    () => (restrictionEnabled ? getOwnerStatus(owner) : null),
    [restrictionEnabled, getOwnerStatus, owner],
  );

  const isPastDueRestricted = !!restrictionStatus?.isLocked;
  const overrideActiveUntil = restrictionStatus?.overrideActiveUntil ?? null;

  const value = useMemo(
    () =>
      shipment &&
      calves &&
      donors &&
      embryos &&
      recipients &&
      semen && {
        shipment,
        calves,
        donors,
        embryos,
        recipients,
        semen,
      },
    [calves, donors, embryos, recipients, semen, shipment],
  );

  return (
    <div className={`common-page ${className}`.trim()}>
      {isPastDueRestricted ? (
        <div className="mb-4 rounded-lg bg-red-500 p-4 text-center font-semibold text-white">
          <span>
            {t('shipping.pastDueRestrictedBanner', {
              account: owner?.name ?? owner?.code ?? '',
            })}
          </span>
        </div>
      ) : isPastDue ? (
        <div className="mb-4 rounded-lg bg-red-500 p-4 text-center font-semibold text-white">
          <span>Past Due Account: {owner?.name}</span>
          {overrideActiveUntil ? (
            <span className="ml-2 font-normal">
              {t('shipping.pastDueOverrideActive', {
                date: overrideActiveUntil,
              })}
            </span>
          ) : null}
        </div>
      ) : isOverAging60 ? (
        <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
          <span>Account Over 60 Days Aging: {owner?.name}</span>
        </div>
      ) : null}
      {value ? (
        <ShipmentContext.Provider value={value}>
          {children}
        </ShipmentContext.Provider>
      ) : (
        <div>Shipment not found.</div>
      )}
    </div>
  );
};

Shipment.Form = memo(ShipmentForm);
Shipment.Toolbar = memo(ShipmentToolBar);
Shipment.EmbryoGrid = memo(ShipmentEmbryoGrid);
Shipment.RecipientGrid = memo(ShipmentRecipientGrid);
Shipment.SemenGrid = memo(ShipmentSemenGrid);
Shipment.CalvesGrid = memo(ShipmentCalvesGrid);
Shipment.DonorsGrid = memo(ShipmentDonorGrid);
Shipment.AdditionalData = memo(ShipmentAdditionalData);
